import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KB } from "@/constants/testIds";
import HintIcon from "@/components/HintIcon";
import { toast } from "sonner";
import { Plus, Save, Trash2, BookOpen, X } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import SearchBar, { useLocalFilter } from "@/components/SearchBar";

const CATEGORIES = [
  ["subscription_plans", "Subscription Plans"],
  ["faqs", "FAQs"],
  ["refund_policy", "Refund Policy"],
  ["pricing", "Pricing"],
  ["features", "Features"],
  ["other", "Other"],
];

export default function KnowledgeBase() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState("");  // KB id armed for delete
  const filtered = useLocalFilter(items, q, ["title", "category", "content"]);
  const load = async () => setItems((await api.get("/kb")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title.trim() || !editing.content.trim()) return toast.error("Title and content required");
    try {
      await api.post("/kb", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  // Two-click confirm — first click arms, second within 5s deletes.
  // Native confirm() is blocked inside the preview iframe.
  const remove = async (id) => {
    if (confirmingDelete !== id) {
      setConfirmingDelete(id);
      setTimeout(() => setConfirmingDelete((cur) => (cur === id ? "" : cur)), 5000);
      return;
    }
    setConfirmingDelete("");
    try {
      await api.delete(`/kb/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={KB.root} className="p-8 lg:p-12 max-w-[1100px]">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">AI Source of Truth</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-orange-600" /> Knowledge Base
            <HintIcon
              testId="kb-page-hint"
              hint={
                <>
                  Articles the AI is allowed to reference when drafting messages or answering user questions.<br /><br />
                  Ground truth: pricing, offers, exam coverage, refund policy, feature lists. Each article can be
                  <b> Active</b> or <b>Draft</b>. Only active articles are injected into AI prompts.<br /><br />
                  The AI Liberty slider on Guardrails controls how strictly the AI must stick to these vs. improvising.
                </>
              }
            />
          </h1>
        </div>
        <div className="flex gap-2">
          <ExportButton path="/exports/kb.xlsx" filename="knowledge_base.xlsx" testId="kb-export-btn" />
          <button
            data-testid={KB.addBtn}
            onClick={() => setEditing({ title: "", category: "faqs", content: "", active: true })}
            className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150"
          >
            <Plus className="h-3.5 w-3.5" /> Add article
          </button>
        </div>
      </div>

      <div className="mb-4"><SearchBar value={q} onChange={setQ} placeholder="Search KB articles…" testId="kb-search" /></div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((a) => (
          <div key={a.id} data-testid={`${KB.itemPrefix}${a.id}`} className="bg-white border border-slate-200 rounded-lg p-5 group">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded">
                {CATEGORIES.find((c) => c[0] === a.category)?.[1] || a.category}
              </span>
              <div className={`flex gap-2 transition-opacity duration-150 ${confirmingDelete === a.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                <button
                  onClick={() => setEditing(a)}
                  className="text-xs font-semibold text-slate-700 hover:text-orange-600"
                >
                  Edit
                </button>
                <button
                  data-testid={`kb-delete-${a.id}`}
                  onClick={() => remove(a.id)}
                  className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded border transition-colors ${
                    confirmingDelete === a.id
                      ? "bg-red-700 text-white border-red-700 animate-pulse"
                      : "text-red-600 border-red-200 bg-red-50 hover:bg-red-100"
                  }`}
                  title={confirmingDelete === a.id ? "Click again to confirm" : "Delete this article"}
                >
                  <Trash2 className="h-3 w-3" />
                  {confirmingDelete === a.id ? "Confirm" : "Delete"}
                </button>
              </div>
            </div>
            <h3 className="font-heading font-bold text-base tracking-tight mb-2">{a.title}</h3>
            <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-5">{a.content}</p>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-2 text-center text-sm text-slate-500 py-12 border border-dashed border-slate-200 rounded-lg">
            No KB articles yet.
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg tracking-tight">{editing.id ? "Edit article" : "New article"}</h3>
              <button onClick={() => setEditing(null)}><X className="h-4 w-4 text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <input
                placeholder="Title"
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
              />
              <select
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
              >
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <textarea
                rows={10}
                placeholder="Article content (markdown supported)"
                value={editing.content}
                onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150 font-mono"
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button data-testid={KB.saveBtn} onClick={save} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-500 transition-colors duration-150">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
